import $http from '../../axios-intance';
import {Notification} from 'element-ui';
import {FILTERABLE_CHARTS} from '../../components/dashboard-b2b/filters';
import {buildColorMaps} from '../../components/dashboard-b2b/colors';

// Gráficas filtrables tal como llegaron en la carga completa (sin filtro): al quitar el filtro se restauran
// de aquí sin volver a consultar el backend.
let unfilteredCharts = null;

// Respuestas ya pedidas con filtro, por "tipo|id": repetir un filtro no vuelve a consultar el backend.
// Se vacía en cada carga completa (cambio de período o "Actualizar").
let filteredChartsCache = {};

function pickFilterableCharts(charts) {
    const picked = {};

    FILTERABLE_CHARTS.forEach((key) => {
        if (charts && charts[key] !== undefined) {
            picked[key] = charts[key];
        }
    });

    return picked;
}

function notifyError(error) {
    Notification.error({
        title: 'Error!',
        message: error.response && error.response.data && error.response.data.message ? error.response.data.message : error.message,
        type: 'error',
        duration: 1500,
        customClass: 'notification-box',
    });
}

// Carga el dashboard completo (kpis + charts) para el período seleccionado. force = true ("Actualizar")
// ignora el cache del backend (DashboardB2bService::remember()). Siempre quita el filtro de drill-down: con
// filtro el backend solo devuelve las gráficas filtrables, sin KPIs.
export async function fetchDashboard({commit, state}, force = false) {
    commit('clearFilter');
    commit('setLoading', true);

    await $http.get('/api/dashboard-b2b', {params: {months: state.params.months, force: force ? 1 : 0}})
        .then((response) => {
            commit('setDashboard', response.data.data);
            unfilteredCharts = pickFilterableCharts(response.data.data.charts);

            const sellers = response.data.data.charts.orders_by_seller || [];
            commit('setSellerMaxValue', sellers.reduce((max, row) => Math.max(max, Number(row.orders_value || 0)), 0));
            commit('setColorMaps', buildColorMaps(response.data.data.charts));
            filteredChartsCache = {};
        })
        .catch(notifyError)
        .finally(() => commit('setLoading', false));
}

// Cambia el período (1, 3 o 6 meses) y recarga.
export async function changeMonths({commit, dispatch}, months) {
    commit('setMonths', months);
    await dispatch('fetchDashboard');
}

// Pide al backend solo las gráficas filtrables con el filtro activo y las fusiona sobre las que ya había, sin
// ocultar el dashboard: esas gráficas muestran su propio "cargando" (filterLoading).
async function fetchFilteredCharts({commit, state}) {
    const filter = state.filter;
    const cacheKey = filter.type + '|' + filter.id;

    if (filteredChartsCache[cacheKey]) {
        commit('mergeCharts', filteredChartsCache[cacheKey]);
        return;
    }

    commit('setFilterLoading', true);

    await $http.get('/api/dashboard-b2b', {
        params: {months: state.params.months, filter_type: filter.type, filter_id: filter.id}
    })
        .then((response) => {
            const charts = response.data.data.charts;

            filteredChartsCache[cacheKey] = charts;

            // Si mientras cargaba se cambió o quitó el filtro, esta respuesta ya no aplica.
            if (state.filter.type === filter.type && state.filter.id === filter.id) {
                commit('mergeCharts', charts);
            }
        })
        .catch(notifyError)
        .finally(() => commit('setFilterLoading', false));
}

// Activa el filtro de drill-down (reemplaza cualquier filtro anterior). filter = { type, id, label }.
export async function applyFilter({commit, state}, filter) {
    if (filter.id === null || filter.id === undefined) {
        return;
    }

    commit('setFilter', filter);
    await fetchFilteredCharts({commit, state});
}

// Quita el filtro: restaura las gráficas de la carga completa (sin llamar al backend).
export async function clearFilter({commit}) {
    commit('clearFilter');

    if (unfilteredCharts) {
        commit('mergeCharts', unfilteredCharts);
    }
}

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Si el servidor (o un proxy) corta la petición antes de que termine el recálculo, este sigue en el backend: se
// consulta su estado cada 10 s (hasta 30 min) y se recarga al terminar.
async function waitRefreshFinished() {
    for (let attempt = 0; attempt < 180; attempt++) {
        await sleep(10000);

        try {
            const response = await $http.get('/api/dashboard-b2b/refresh-status');

            if (!response.data.data.running) {
                return true;
            }
        } catch (error) {
            // Error puntual de red: se sigue esperando.
        }
    }

    return false;
}

// Botón "Actualizar" (ya confirmado por el usuario): recalcula las tablas de estadísticas y recarga el dashboard.
export async function refreshStats({commit, dispatch}) {
    commit('setRefreshing', true);

    try {
        await $http.post('/api/dashboard-b2b/refresh');

        Notification.success({
            title: 'Listo',
            message: 'Las estadísticas del dashboard se recalcularon correctamente',
            type: 'success',
            duration: 2500,
            customClass: 'notification-box',
        });
    } catch (error) {
        // Con respuesta del backend (sin permiso, ya hay otro recálculo, error SQL): se muestra el mensaje.
        if (error.response && [403, 500].includes(error.response.status)) {
            notifyError(error);
            commit('setRefreshing', false);
            return;
        }

        // Sin respuesta (tiempo de espera del servidor/proxy): el recálculo sigue; se espera a que termine.
        const finished = await waitRefreshFinished();

        if (!finished) {
            notifyError({message: 'El recálculo sigue en proceso. Recargue la página en unos minutos.'});
            commit('setRefreshing', false);
            return;
        }
    }

    commit('setRefreshing', false);
    await dispatch('fetchDashboard');
}
